import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Cpu, MapPin, Radar } from 'lucide-react';
interface SlidePropositionProps {
  onContinue: () => void;
}
export function SlideProposition({
  onContinue
}: SlidePropositionProps) {
  const [stage, setStage] = useState(0);
  const pillars = [{
    title: 'Centralize the campaign engine',
    desc: 'Strategy, narrative, claims, assets, workflows, compliance and measurement built once.',
    icon: <Cpu className="w-6 h-6 text-teal-400" data-id="element-927" data-mp="24jm7ms3cno0r" />
  }, {
    title: 'Decentralize local execution',
    desc: 'Markets adapt language, emphasis, channels and calls to action for their own HCPs.',
    icon: <MapPin className="w-6 h-6 text-sky-400" data-id="element-928" data-mp="1vio1o49cp6fj" />
  }, {
    title: 'Give HQ full visibility',
    desc: 'The Control Tower shows what is live, how it performs and what should be reused.',
    icon: <Radar className="w-6 h-6 text-purple-500" data-id="element-929" data-mp="3pwp38wwac7jb" />
  }];
  useEffect(() => {
    const timers = [300, 800, 1300, 1800, 2500].map((ms, idx) => setTimeout(() => setStage(idx + 1), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
  return <div className="h-full w-full flex flex-col items-center justify-center px-8 py-10 md:px-20 relative overflow-hidden" data-id="element-930" data-mp="1o4wht4vcugvv">
      <div className="absolute inset-0 z-0 flex items-center justify-center pointer-events-none" data-id="element-931" data-mp="1xx29edhp2waj">
        <div className="w-[700px] h-[700px] bg-teal-400/5 rounded-full blur-[100px]" data-id="element-932" data-mp="1a6y6678f4yk2" />
      </div>

      <div className="z-10 max-w-6xl w-full flex flex-col items-center" data-id="element-933" data-mp="158eio2e6fepl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center space-y-4 mb-10" data-id="element-934" data-mp="15a7t7srwuj2v">
          <p className="text-teal-400 font-semibold tracking-widest uppercase text-sm" data-id="element-935" data-mp="2umjo1hynnsod">
            The Proposition
          </p>
          <h2 className="text-4xl md:text-6xl font-serif text-white leading-tight" data-id="element-936" data-mp="2pn6phzyf8x5y">
            Scale and relevance,
            <br data-id="element-937" data-mp="1naaigzz0whe3" />
            in one operating model
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full" data-id="element-938" data-mp="1s8c49snu2ut9">
          {pillars.map((pillar, idx) => <motion.div key={pillar.title} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= idx + 1 ? 1 : 0,
          y: stage >= idx + 1 ? 0 : 20
        }} className="bg-navy-700/40 border border-navy-600 rounded-2xl p-7 h-full flex flex-col" data-id="element-939" data-mp="1wc1knuucu637" data-mpi={idx}>
              <div className="w-12 h-12 rounded-xl bg-teal-400/10 flex items-center justify-center mb-5" data-id="element-940" data-mp="1dinxwd0sp5yo">
                {pillar.icon}
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-941" data-mp="kwv1cl63enkc">
                {pillar.title}
              </h3>
              <p className="text-white/60 leading-relaxed text-sm flex-1" data-id="element-942" data-mp="ptv8kzeohwd1">
                {pillar.desc}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        width: 0,
        opacity: 0
      }} animate={{
        width: stage >= 4 ? 180 : 0,
        opacity: stage >= 4 ? 1 : 0
      }} transition={{
        duration: 0.7
      }} className="h-[3px] bg-gradient-to-r from-blue-400 via-teal-400 to-teal-300 rounded-full my-10" data-id="element-943" data-mp="dcaqiyrm5t7c" />

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: stage >= 5 ? 1 : 0,
        y: stage >= 5 ? 0 : 12
      }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-3xl" data-id="element-944" data-mp="3qr72q7g1byjp">
          {['Global consistency', 'Local relevance', 'Faster execution'].map(item => <div key={item} className="bg-navy-900/50 border border-teal-400/30 rounded-2xl px-5 py-4 text-center" data-id="element-945" data-mp="37scm0sx0go0w">
                <p className="text-white font-semibold" data-id="element-946" data-mp="1fr1v9lk3jbr1">{item}</p>
              </div>)}
        </motion.div>
      </div>
    </div>;
}